<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { applyColorTheme } from 'dashboard/helper/themeHelper';
import {
  getBrandColors,
  setBrandColors,
} from 'dashboard/helper/brandThemeHelper';

const { t } = useI18n();

const dialogRef = ref(null);
const colorScheme = ref('auto');
const brandColors = ref(getBrandColors());

const themeOptions = [
  { key: 'light', icon: 'i-lucide-sun', labelKey: 'LIGHT_MODE' },
  { key: 'dark', icon: 'i-lucide-moon', labelKey: 'DARK_MODE' },
  { key: 'auto', icon: 'i-lucide-monitor', labelKey: 'SYSTEM_MODE' },
];

const setScheme = scheme => {
  colorScheme.value = scheme;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, scheme);
  const isOSDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const resolved =
    (scheme === 'auto' && isOSDark) || scheme === 'dark' ? 'dark' : 'light';
  applyColorTheme(resolved);
};

const primaryColor = computed({
  get: () => brandColors.value.primary,
  set: color => {
    brandColors.value = setBrandColors({ primary: color });
  },
});

const secondaryColor = computed({
  get: () => brandColors.value.secondary,
  set: color => {
    brandColors.value = setBrandColors({ secondary: color });
  },
});

const open = () => {
  colorScheme.value =
    LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto';
  brandColors.value = getBrandColors();
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('COMMAND_BAR.COMMANDS.CHANGE_APPEARANCE')"
    width="sm"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-5">
      <!-- Theme selector -->
      <div class="flex flex-col gap-2">
        <p class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
          {{ t('COMMAND_BAR.SECTIONS.APPEARANCE') }}
        </p>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="opt in themeOptions"
            :key="opt.key"
            type="button"
            class="flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border text-xs font-medium transition-colors"
            :class="
              colorScheme === opt.key
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-slate-4 hover:border-n-slate-7 text-n-slate-11 hover:text-n-slate-12'
            "
            @click="setScheme(opt.key)"
          >
            <span :class="[opt.icon, 'size-5']" />
            {{ t(`COMMAND_BAR.COMMANDS.${opt.labelKey}`) }}
          </button>
        </div>
      </div>

      <!-- Color pickers -->
      <div class="flex flex-col gap-3">
        <p class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
          {{ t('COMMAND_BAR.COMMANDS.PRIMARY_COLOR') }} &amp;
          {{ t('COMMAND_BAR.COMMANDS.SECONDARY_COLOR') }}
        </p>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs text-n-slate-11">
              {{ t('COMMAND_BAR.COMMANDS.PRIMARY_COLOR') }}
            </span>
            <ColorPicker v-model="primaryColor" />
          </div>
          <div class="flex flex-col gap-1.5 min-w-0">
            <span class="text-xs text-n-slate-11">
              {{ t('COMMAND_BAR.COMMANDS.SECONDARY_COLOR') }}
            </span>
            <ColorPicker v-model="secondaryColor" />
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
